<template>
  <dp-seite titel="Musterzuordnung" untertitel="Kolonnen, Maschinen und einzelne Mitarbeiter in einem Ablauf" :snack="snack" :zeige-entwurf="false">
    <template #aktionen>
      <v-chip small outlined color="primary">Entwurf · Rev. {{ dp.plan ? dp.plan.revision : '–' }}</v-chip>
      <v-btn small depressed @click="$router.push('/dienstplanung/kalender')"><v-icon small left>mdi-calendar-month</v-icon>Planungskalender</v-btn>
      <v-btn small depressed color="success" :disabled="!dirty || !settings.can_edit" @click="speichernOeffnen">
        <v-icon small left>mdi-content-save</v-icon>Entwurf speichern<span v-if="dirty">&nbsp;({{ dirty }})</span>
      </v-btn>
    </template>
    <template #leiste>
      <div class="dp-steps">
        <button v-for="(s, i) in schritte" :key="i" :class="{active: aktiv === i + 1}" @click="springe(i + 1)">
          <span class="dot">{{ i + 1 }}</span>{{ s }}
        </button>
      </div>
    </template>

    <template v-if="dp.plan">
      <!-- 1 -->
      <section id="schritt-1" ref="s1" class="dp-sec">
        <h2><span class="num">1</span>Zuordnungsart, Ziel &amp; Planungszeitraum <small>Nur die gewählten Kalenderwochen werden geändert</small></h2>
        <div class="d-flex flex-wrap align-center" style="gap: 12px">
          <v-select v-model="ed.typ" :items="typen" outlined dense hide-details label="Zuordnungsart" style="max-width: 230px"/>
          <v-autocomplete v-model="ed.ziel[ed.typ]" :items="zielItems" outlined dense hide-details :label="zielLabel" style="min-width: 300px; max-width: 420px"
                          :filter="ed.typ === 'DIRECT' ? mitaFilter : undefined" :search-input.sync="zielSuche"
                          :no-data-text="ed.typ === 'MACHINE' ? 'Keine Maschine in einem aktiven Maschinenmodell.' : 'Keine Einträge.'">
            <!-- employees: same order and units as "Mitarbeiter zuweisen", as a list -->
            <template v-if="ed.typ === 'DIRECT'" #item="{item}">
              <v-list-item-icon class="mr-3"><v-icon small>mdi-account</v-icon></v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title>{{ item.text }}</v-list-item-title>
                <v-list-item-subtitle>{{ mitaSub(item) }}</v-list-item-subtitle>
              </v-list-item-content>
            </template>
          </v-autocomplete>
        </div>
        <div class="mt-3"><zeitraum-wahl :value="dp.range" @input="r => reloadPlan(r)"/></div>
        <div class="dp-hint mt-2">
          <v-icon small color="primary">{{ ed.typ === 'CREW' ? 'mdi-account-group' : ed.typ === 'MACHINE' ? 'mdi-truck' : 'mdi-account' }}</v-icon>
          <span v-if="ed.typ === 'CREW'">Zuerst die Musterwochen der Kolonne zuordnen, dann in Schritt 4 die Mitarbeiter der Woche festlegen. Die Pool-Mitgliedschaft allein plant niemanden ein.</span>
          <span v-else-if="ed.typ === 'MACHINE'"><template v-if="ohneModell.length"><b>{{ ohneModell.join(', ') }}</b> {{ ohneModell.length === 1 ? 'gehört' : 'gehören' }} zu keinem
            Maschinenmodell und {{ ohneModell.length === 1 ? 'ist' : 'sind' }} daher nicht wählbar (Planungsgruppen › Maschinenmodelle). </template>Betriebszeiten der Maschine ungeraden oder geraden ISO-Wochen zuordnen. Die Fahrerpositionen verbinden die Maschinendienste zu einer Mitarbeiterwoche.</span>
          <span v-else>Dem Mitarbeiter eine Musterwoche direkt zuordnen, ohne Kolonne. Eine bestehende Wochenquelle (Kolonne, Maschine) wird dabei ausdrücklich ersetzt.</span>
        </div>
      </section>

      <template v-if="zielId">
        <!-- 2 -->
        <section id="schritt-2" ref="s2" class="dp-sec">
          <h2><span class="num">2</span>Musterwoche auswählen <small>Vorhandene Arbeitszeitraster – nur lesen</small></h2>
          <wochen-browser v-model="ed.gewaehlt" :preset="ed.preset"/>
        </section>

        <!-- 3 -->
        <section id="schritt-3" ref="s3" class="dp-sec">
          <h2><span class="num">3</span>Kalenderwochen zuordnen <small>{{ wochen.length }} Kalenderwochen im Zeitraum</small></h2>
          <div v-if="ed.typ === 'MACHINE'" class="d-flex align-center flex-wrap mb-3" style="gap: 10px">
            <span class="text-body-2">Anwenden auf</span>
            <v-btn-toggle v-model="ed.paritaet" mandatory dense color="primary">
              <v-btn small value="ODD">Ungerade ISO-Wochen</v-btn>
              <v-btn small value="EVEN">Gerade ISO-Wochen</v-btn>
              <v-btn small value="ALL">Jede Woche</v-btn>
            </v-btn-toggle>
            <span class="text-caption grey--text">{{ wochen.filter(w => ed.paritaet === 'ALL' || w.paritaet === ed.paritaet).length }} passende Wochen</span>
          </div>
          <div class="d-flex align-center flex-wrap mb-2" style="gap: 6px">
            <span class="text-caption grey--text">Dieselbe Musterwoche kann mehreren Kalenderwochen zugeordnet werden.</span>
            <v-spacer/>
            <v-btn x-small text color="primary" @click="alleWaehlen">Alle wählbaren</v-btn>
            <v-btn x-small text color="primary" @click="ed.checked = []">Auswahl leeren</v-btn>
          </div>
          <kw-karten v-model="ed.checked" :wochen="wochen" :ziel-typ="ed.typ" :ziel-id="zielId" :gewaehlt="ed.gewaehlt"
                     :paritaet="ed.typ === 'MACHINE' ? ed.paritaet : 'ALL'" @undo="rueckgaengig" @preview="ansehen"/>
          <div class="dp-actions">
            <div class="text-body-2"><b>{{ ed.checked.length }}</b> Kalenderwoche(n) gewählt
              <span class="grey--text">· {{ vorgemerktFuerZiel }} vorgemerkt für {{ zielName }}</span></div>
            <v-spacer/>
            <v-btn small text color="error" :disabled="!ed.checked.length || !settings.can_edit" @click="entfernen">Zuordnung entfernen</v-btn>
            <v-btn color="primary" depressed :disabled="!ed.gewaehlt || !ed.checked.length || !settings.can_edit" @click="zuordnen">
              <v-icon left>mdi-arrow-right</v-icon>Musterwoche zuordnen
            </v-btn>
          </div>
        </section>

        <!-- 4 -->
        <section id="schritt-4" ref="s4" class="dp-sec">
          <template v-if="ed.typ === 'CREW'">
            <h2><span class="num">4</span>Mitarbeiter der Kolonne festlegen <small>Pool-Mitgliedschaft plant niemanden automatisch ein</small></h2>
            <div class="d-flex align-center flex-wrap mb-2" style="gap: 12px">
              <v-select v-model="ed.fokusWoche" :items="wochenItems" outlined dense hide-details label="Kalenderwoche" style="max-width: 280px"/>
              <v-chip small color="primary" outlined>{{ kolonneTeam.filter(r => r.hier).length }} eingeplant</v-chip>
              <span class="text-body-2">{{ fokusZuordnung ? zuordnungLabel(fokusZuordnung) : 'Keine Musterwoche für diese Woche' }}</span>
              <v-spacer/>
              <v-btn small text color="primary" :disabled="!vorwocheVorhanden" @click="wieVorwoche"><v-icon small left>mdi-content-copy</v-icon>Wie Vorwoche</v-btn>
            </div>
            <v-alert v-if="!fokusZuordnung" type="warning" dense outlined>Bitte zuerst in Schritt 3 der Kolonne eine Musterwoche für {{ fokusWocheLabel }} zuordnen (vorgemerkt genügt).</v-alert>
            <v-simple-table dense class="dp-table">
              <thead><tr><th style="width: 46px">Einplanen</th><th>Berechtigter Mitarbeiter</th><th>Weitere Pools</th><th>Aktuelle / vorgemerkte Wochenquelle</th><th>Ergebnis</th></tr></thead>
              <tbody>
              <tr v-for="r in kolonneTeam" :key="r.mita" :class="{chosen: auswahlTeam.includes(r.mita)}">
                <td><v-simple-checkbox :value="auswahlTeam.includes(r.mita)" :disabled="!r.berechtigt || !fokusZuordnung || !settings.can_edit" @input="v => toggleTeam(r.mita, v)"/></td>
                <td class="font-weight-medium">{{ name(r.mita) }}<v-chip v-if="isStagedB(r.mita, ed.fokusWoche)" x-small color="success" dark class="ml-1">vorgemerkt</v-chip></td>
                <td class="text-caption">{{ r.weitere }}</td>
                <td><v-chip x-small :color="r.quelle ? 'primary' : 'orange'" outlined>{{ r.quelle ? quellen[r.quelle.typ] : 'keine' }}</v-chip> <span class="text-caption">{{ quelleLabel(r.quelle) }}</span></td>
                <td class="text-caption">{{ r.hier ? 'folgt ' + zuordnungLabel(fokusZuordnung) : r.quelle ? 'Auswahl ersetzt die bisherige Quelle' : 'verfügbar' }}</td>
              </tr>
              <tr v-if="!kolonneTeam.length"><td colspan="5" class="grey--text">Der Pool der Kolonne ist leer. Mitarbeiter unter Planungsgruppen hinzufügen.</td></tr>
              </tbody>
            </v-simple-table>
            <div class="dp-actions">
              <v-checkbox v-model="alleWochen" dense hide-details label="Für alle Wochen des Zeitraums übernehmen, in denen die Kolonne eine Musterwoche hat"/>
              <v-spacer/>
              <v-btn color="primary" depressed :disabled="!fokusZuordnung || !settings.can_edit" @click="teamVormerken"><v-icon left>mdi-account-multiple-check</v-icon>Besetzung vormerken</v-btn>
            </div>
          </template>

          <template v-else-if="ed.typ === 'MACHINE'">
            <h2><span class="num">4</span>Fahrer einteilen &amp; Abdeckung prüfen <small>Eine kombinierte Mitarbeiterwoche, nicht eine Zuordnung je Maschine</small></h2>
            <div class="dp-hint mb-2">Die Fahrerpositionen gelten für alle Maschinen des Modells <b>{{ modell ? modell.bezeichnung : '' }}</b>. Ein Fahrer übernimmt die Tage seiner Position laut Matrix – nicht alle Betriebsstunden einer Maschine.</div>
            <v-alert v-if="!modell" type="warning" dense outlined>Diese Maschine gehört zu keinem aktiven Maschinenmodell (Planungsgruppen › Maschinenmodell).</v-alert>
            <template v-else>
              <div class="d-flex align-center flex-wrap mb-2" style="gap: 12px">
                <v-select v-model="ed.fokusWoche" :items="wochenItems" outlined dense hide-details label="Kalenderwoche" style="max-width: 280px"/>
                <v-chip small outlined color="primary">{{ fokusInfo ? parityText(fokusInfo.paritaet) + 'e ISO-Woche' : '' }}</v-chip>
                <v-spacer/>
                <v-btn small text color="primary" :disabled="!vorwocheVorhanden" @click="wieVorwoche"><v-icon small left>mdi-content-copy</v-icon>Wie Vorwoche</v-btn>
              </div>
              <v-alert v-if="fehlendeMaschinen.length" type="warning" dense outlined class="mb-2">
                <div class="d-flex align-center flex-wrap" style="gap: 8px">
                  <span>Ohne Musterwoche im Zeitraum: {{ fehlendeMaschinen.map(f => gruppeName(f.id) + ' (' + f.wochen.length + ' KW)').join(', ') }}.
                    Fahrerdienste auf diesen Maschinen können nicht aufgelöst werden.</span>
                  <v-spacer/>
                  <v-btn small depressed color="primary" :disabled="!settings.can_edit" @click="maschinenErgaenzen">Mit Standardraster ergänzen</v-btn>
                </div>
              </v-alert>
              <v-simple-table dense class="dp-table">
                <thead><tr><th>Fahrerposition</th><th style="min-width: 260px">Mitarbeiter in {{ fokusWocheLabel }}</th><th>Dienste der Woche</th><th>Stunden</th><th></th></tr></thead>
                <tbody>
                <tr v-for="p in modell.positionen" :key="p.id">
                  <td class="font-weight-medium">{{ p.bezeichnung }}</td>
                  <td>
                    <v-autocomplete :value="inhaber(p.id)" :items="fahrerItems(p.id)" dense hide-details clearable placeholder="unbesetzt" :disabled="!settings.can_edit"
                                    @change="v => fahrerSetzen(p.id, v)"/>
                  </td>
                  <td class="text-caption">{{ dienstText(p.id) }}<div v-for="x in positionWoche(p.id, ed.fokusWoche).probleme" :key="x" class="red--text">{{ x }}</div></td>
                  <td>{{ hhmm(sum(positionWoche(p.id, ed.fokusWoche).tage)) }}</td>
                  <td><v-btn x-small text color="primary" @click="positionAnsehen(p)">Ansehen</v-btn></td>
                </tr>
                </tbody>
              </v-simple-table>
              <div class="dp-actions">
                <v-select v-model="fahrerAlle" :items="fahrerUebernahme" dense hide-details outlined style="max-width: 360px" label="Änderungen anwenden auf"/>
                <span class="text-caption grey--text">Fahrerwechsel werden für diese Auswahl vorgemerkt.</span>
              </div>
              <h3 class="text-subtitle-2 mt-4 mb-1">Abdeckung der Maschinentage in {{ fokusWocheLabel }}</h3>
              <v-simple-table dense class="dp-table text-center">
                <thead><tr><th class="text-left">Maschine</th><th v-for="t in wtKurz" :key="t">{{ t }}</th></tr></thead>
                <tbody>
                <tr v-for="g in modell.gruppen" :key="g.planungsgruppe_id">
                  <td class="text-left font-weight-medium">{{ gruppeName(g.planungsgruppe_id) }}</td>
                  <td v-for="(t, i) in wtKurz" :key="t"><div class="slot" :class="slot(g.planungsgruppe_id, i).cls">{{ slot(g.planungsgruppe_id, i).text }}<small>{{ slot(g.planungsgruppe_id, i).sub }}</small></div></td>
                </tr>
                </tbody>
              </v-simple-table>
            </template>
          </template>

          <template v-else>
            <h2><span class="num">4</span>Direkte Zuordnung prüfen <small>Keine Kolonne oder Fahrerposition nötig</small></h2>
            <v-simple-table dense class="dp-table">
              <thead><tr><th>Kalenderwoche</th><th>Gespeicherte Wochenquelle</th><th>Vorgemerkte direkte Musterwoche</th><th>Wirkung</th></tr></thead>
              <tbody>
              <tr v-for="w in wochen" :key="w.key">
                <td>{{ weekLabel(w) }}</td>
                <td>{{ quelleLabel(savedB(w.key)) }}</td>
                <td>{{ stagedZ(w.key) && !stagedZ(w.key).loeschen ? zuordnungLabel(stagedZ(w.key)) : '–' }}</td>
                <td><v-chip v-if="stagedZ(w.key)" x-small outlined :color="savedB(w.key) ? 'orange darken-2' : 'success'">{{ stagedZ(w.key).loeschen ? 'wird entfernt' : savedB(w.key) && savedB(w.key).typ !== 'DIRECT' ? 'ersetzt bisherige Quelle' : 'neue direkte Zuordnung' }}</v-chip></td>
              </tr>
              </tbody>
            </v-simple-table>
          </template>
        </section>
      </template>

      <!-- 5 -->
      <section id="schritt-5" ref="s5" class="dp-sec">
        <h2><span class="num">5</span>Prüfen &amp; speichern <small>Nur der Entwurf ändert sich – die Veröffentlichung ist ein eigener Schritt</small></h2>
        <div class="dp-metrics">
          <div><b>{{ zaehle('Musterwoche') }}</b>Musterwochen-Zuordnungen</div>
          <div><b>{{ zaehle('Wochenquelle') }}</b>Wochenquellen von Mitarbeitern</div>
          <div><b>{{ betroffeneWochen }}</b>betroffene Kalenderwochen</div>
        </div>
        <aenderungen-tabelle :rows="rows"/>
        <div class="dp-actions">
          <div class="text-caption grey--text"><b>Erst speichern, dann unter „Prüfen &amp; veröffentlichen“ freigeben.</b><br>Jahresraster, erfasste Zeiten und veröffentlichte Stände ändert dieses Formular nie.</div>
          <v-spacer/>
          <v-btn text :disabled="!dirty" @click="$refs.verwerfen.open()"><v-icon left>mdi-undo</v-icon>Verwerfen</v-btn>
          <v-btn depressed @click="$router.push('/dienstplanung/veroeffentlichen')"><v-icon left>mdi-eye-check-outline</v-icon>Prüfen &amp; veröffentlichen</v-btn>
          <v-btn color="success" depressed :disabled="!dirty || !settings.can_edit" @click="speichernOeffnen"><v-icon left>mdi-content-save</v-icon>Entwurf speichern</v-btn>
        </div>
      </section>
    </template>

    <bestaetigen-dialog ref="speichern" titel="Entwurf speichern" ok-text="Speichern" ok-farbe="success" breit :busy="busy"
                        :pflicht-text="ersetzt ? 'Ich bestätige, dass die markierten bestehenden Zuordnungen bzw. Wochenquellen für diese Wochen ersetzt werden.' : ''"
                        @ok="speichern">
      <p class="text-body-2">{{ dirty }} vorgemerkte Änderung(en) werden als ein Entwurfsstand gespeichert. Andere Wochen bleiben unverändert.</p>
      <aenderungen-tabelle :rows="rows"/>
    </bestaetigen-dialog>
    <bestaetigen-dialog ref="verwerfen" titel="Vorgemerkte Änderungen verwerfen?" ok-text="Verwerfen" ok-farbe="error" @ok="verwerfen">
      <p class="text-body-2">Alle {{ dirty }} vorgemerkten Änderungen werden verworfen. Der gespeicherte Entwurf bleibt unverändert.</p>
    </bestaetigen-dialog>
    <bestaetigen-dialog ref="team" titel="Besetzung vormerken" ok-text="Vormerken" breit
                        :pflicht-text="teamErsetzt ? 'Bestehende Wochenquellen der markierten Mitarbeiter werden für diese Wochen ersetzt bzw. entfernt.' : ''"
                        @ok="teamUebernehmen">
      <aenderungen-tabelle :rows="teamRows"/>
      <v-alert type="info" dense text class="mt-3 mb-0">Eine frühere Kolonne verliert dadurch ggf. Besetzung, eine Fahrerposition kann frei werden. Fehlende Besetzungen bleiben sichtbar – es gibt keinen automatischen Ersatz.</v-alert>
    </bestaetigen-dialog>
    <bestaetigen-dialog ref="ansicht" :titel="ansichtTitel" breit>
      <tages-raster v-if="ansichtTage" :tage="ansichtTage" :montag="ansichtMontag" compact show-schicht/>
    </bestaetigen-dialog>
  </dp-seite>
</template>

<script>
import {fmtHHMM, inPhase, parityText, periodWeeks, QUELLEN, weekHours, weekInfo, weekLabel, WOCHENTAGE_KURZ} from '@/shared/dienstplan_api'
import seite from './seite'
import {changeRows, dirtyCount, discardStaged, dp, effBesetzung, effZuordnung, errorText, gruppe, gruppeName, inPool, isStagedB, kolonnen, machineEligible,
  loadStruktur, maschinen, mitarbeiterListe, modellVonMaschine, name, positionWoche, quelleLabel, rasterWochen, saveDraft, savedBesetzung, stageBesetzung, stageZuordnung, zKey, zuordnungLabel} from './store'
import DpSeite from './components/DpSeite'
import ZeitraumWahl from './components/ZeitraumWahl'
import WochenBrowser from './components/WochenBrowser'
import KwKarten from './components/KwKarten'
import AenderungenTabelle from './components/AenderungenTabelle'
import BestaetigenDialog from './components/BestaetigenDialog'
import TagesRaster from './components/TagesRaster'

export default {
  name: 'DpZuordnung',
  mixins: [seite],
  components: {DpSeite, ZeitraumWahl, WochenBrowser, KwKarten, AenderungenTabelle, BestaetigenDialog, TagesRaster},
  data: () => ({
    schritte: ['Art & Zeitraum', 'Musterwoche', 'Kalenderwochen', 'Besetzung & Abdeckung', 'Prüfen & speichern'],
    typen: [{value: 'CREW', text: 'Kolonne / Team'}, {value: 'MACHINE', text: 'Maschine'}, {value: 'DIRECT', text: 'Einzelner Mitarbeiter'}],
    aktiv: 1,
    zielSuche: null,
    busy: false,
    auswahlTeam: [],
    alleWochen: false,
    teamPlan: [],
    fahrerAlle: 'WOCHE',
    ansichtTitel: '',
    ansichtTage: null,
    ansichtMontag: '',
    quellen: QUELLEN,
    wtKurz: WOCHENTAGE_KURZ,
  }),
  computed: {
    ed: () => dp.editor,
    dirty: () => dirtyCount(),
    rows: () => changeRows(),
    ersetzt () {
      return this.rows.some(r => r.ersetzt)
    },
    zielId () {
      return this.ed.ziel[this.ed.typ]
    },
    zielLabel () {
      return {CREW: 'Kolonne / Team', MACHINE: 'Maschine', DIRECT: 'Mitarbeiter'}[this.ed.typ]
    },
    zielItems () {
      if (this.ed.typ === 'CREW') return kolonnen().map(g => ({value: g.id, text: g.nummer + ' · ' + g.bezeichnung + (g.typ === 'TEAM' ? ' (Team)' : '')}))
      if (this.ed.typ === 'MACHINE') {
        // machines outside a model have no driver positions: shown, but not selectable
        return maschinen().map(g => modellVonMaschine(g.id) ? {value: g.id, text: g.bezeichnung}
          : {value: g.id, text: g.bezeichnung + ' – in keinem Maschinenmodell', disabled: true})
          .sort((a, b) => (a.disabled === b.disabled ? a.text.localeCompare(b.text) : a.disabled ? 1 : -1))
      }
      return mitarbeiterListe()
    },
    ohneModell () {
      return maschinen().filter(g => !modellVonMaschine(g.id)).map(g => g.bezeichnung)
    },
    zielName () {
      return this.ed.typ === 'DIRECT' ? name(this.zielId) : gruppeName(this.zielId)
    },
    wochen () {
      return periodWeeks(dp.range.von, dp.range.bis)
    },
    wochenItems () {
      return this.wochen.map(w => ({value: w.key, text: weekLabel(w)}))
    },
    fokusInfo () {
      return weekInfo(this.ed.fokusWoche)
    },
    fokusWocheLabel () {
      return this.fokusInfo ? 'KW ' + this.fokusInfo.nummer : ''
    },
    fokusZuordnung () {
      return this.zielId && this.ed.fokusWoche ? effZuordnung(this.ed.typ, this.zielId, this.ed.fokusWoche) : null
    },
    vorgemerktFuerZiel () {
      return Object.values(dp.staged.zuordnungen).filter(z => z.ziel_typ === this.ed.typ && z.ziel_id === this.zielId).length
    },
    betroffeneWochen () {
      return new Set(this.rows.map(r => r.woche)).size
    },
    modell () {
      return this.ed.typ === 'MACHINE' && this.zielId ? modellVonMaschine(this.zielId) : null
    },
    // crew members: eligible pool of the week plus everybody currently in the crew
    kolonneTeam () {
      const g = gruppe(this.zielId)
      if (!g || this.ed.typ !== 'CREW' || !this.ed.fokusWoche) return []
      const w = this.ed.fokusWoche
      const ids = new Set(g.mitglieder.map(m => m.mita_bis_id))
      dp.basis.mitarbeiter.forEach(m => {
        const b = effBesetzung(m.bis_id, w)
        if (b && b.typ === 'CREW' && b.planungsgruppe_id === g.id) ids.add(m.bis_id)
      })
      return [...ids].map(mita => {
        const quelle = effBesetzung(mita, w)
        return {
          mita, quelle,
          hier: !!quelle && quelle.typ === 'CREW' && quelle.planungsgruppe_id === g.id,
          berechtigt: inPool(g.id, mita, w),
          weitere: kolonnen().filter(k => k.id !== g.id && k.mitglieder.some(m => m.mita_bis_id === mita)).map(k => k.bezeichnung).join(', '),
        }
      }).sort((a, b) => name(a.mita).localeCompare(name(b.mita)))
    },
    teamRows () {
      return this.teamPlan.map(t => ({art: 'Wochenquelle', ziel: name(t.mita), woche: t.woche, vorher: quelleLabel(t.vorher),
        nachher: t.next ? gruppeName(this.zielId) : 'Keine Wochenquelle', ersetzt: !!t.vorher}))
    },
    teamErsetzt () {
      return this.teamPlan.some(t => t.vorher)
    },
    vorwocheVorhanden () {
      const i = this.wochen.findIndex(w => w.key === this.ed.fokusWoche)
      return i > 0
    },
    // machines of the model without a pattern week in some weeks of the range
    fehlendeMaschinen () {
      if (!this.modell) return []
      return this.modell.gruppen.map(g => ({id: g.planungsgruppe_id, azra: g.standard_azra,
        wochen: this.wochen.filter(w => !effZuordnung('MACHINE', g.planungsgruppe_id, w.key)).map(w => w.key)})).filter(f => f.wochen.length)
    },
    fahrerUebernahme () {
      const p = this.fokusInfo ? parityText(this.fokusInfo.paritaet) : ''
      return [{value: 'WOCHE', text: 'nur ' + this.fokusWocheLabel}, {value: 'PARITAET', text: 'alle ' + p + 'en Wochen des Zeitraums'}, {value: 'ALLE', text: 'alle Wochen des Zeitraums'}]
    },
  },
  watch: {
    'ed.typ' () {
      this.ed.checked = []
      this.ensureZiel()
      this.ed.preset = this.defaultPreset()
    },
    zielId () {
      this.ed.checked = []
      this.ed.preset = this.defaultPreset()
      this.syncTeam()
    },
    'ed.fokusWoche' () {
      this.syncTeam()
    },
    wochen: {
      immediate: true,
      handler (ws) {
        if (ws.length && !ws.some(w => w.key === this.ed.fokusWoche)) this.ed.fokusWoche = ws[0].key
      },
    },
  },
  created () {
    // the employee list comes from the organisation tree: wait for it before the default target
    Promise.all([this.ensure(), loadStruktur().catch(() => {})]).then(() => {
      // default target first: its watcher must run before the query preset is applied
      if (!this.$route.query.ziel) this.ensureZiel()
      if (!this.ed.preset) this.ed.preset = this.defaultPreset()
      this.fromQuery()
      this.syncTeam()
    })
  },
  mounted () {
    this.onScroll = () => {
      const secs = [1, 2, 3, 4, 5].map(n => this.$el.querySelector('#schritt-' + n)).filter(Boolean)
      const cur = secs.filter(s => s.getBoundingClientRect().top < 200).pop()
      this.aktiv = cur ? Number(cur.id.split('-')[1]) : 1
    }
    this.$nextTick(() => {
      const body = this.$el.querySelector('.dp-body')
      if (body) body.addEventListener('scroll', this.onScroll, {passive: true})
    })
  },
  beforeDestroy () {
    const body = this.$el && this.$el.querySelector('.dp-body')
    if (body) body.removeEventListener('scroll', this.onScroll)
  },
  methods: {
    name,
    gruppeName,
    zuordnungLabel,
    quelleLabel,
    isStagedB,
    positionWoche,
    parityText,
    weekLabel,
    hhmm: fmtHHMM,
    sum: weekHours,
    savedB: w => savedBesetzung(dp.editor.ziel.DIRECT, w),
    stagedZ (w) {
      return dp.staged.zuordnungen[zKey('DIRECT', this.zielId, w)]
    },
    zaehle (art) {
      return this.rows.filter(r => r.art === art).length
    },
    // search by name, Pers.-Nr. or unit; unit headers only show without a search,
    // so the unit is repeated in the row while searching
    mitaFilter (item, query) {
      if (item.header) return false
      const q = String(query).toLowerCase()
      return [item.text, item.pers, item.einheit].some(t => String(t || '').toLowerCase().includes(q))
    },
    mitaSub (item) {
      const pers = item.pers && !item.text.includes(item.pers) ? 'Pers.-Nr. ' + item.pers : ''
      // with a selection Vuetify puts its text into the search field: not a search
      const suche = this.zielSuche && this.zielSuche !== name(this.zielId)
      return [pers, suche ? item.einheit : ''].filter(Boolean).join(' · ')
    },
    ensureZiel () {
      const first = this.zielItems.find(i => !i.disabled && !i.header)
      if (!this.zielId && first) this.ed.ziel[this.ed.typ] = first.value
    },
    springe (n) {
      const el = this.$el.querySelector('#schritt-' + n)
      if (el) el.scrollIntoView({behavior: 'smooth', block: 'start'})
    },
    // links from calendar / staffing / reference pages
    fromQuery () {
      const q = this.$route.query
      if (q.typ && this.typen.some(t => t.value === q.typ)) this.ed.typ = q.typ
      if (q.ziel) this.ed.ziel[this.ed.typ] = q.ziel
      this.ensureZiel()
      // the type/target watchers reset selection and preset; apply the rest after them
      this.$nextTick(() => {
        if (q.woche && weekInfo(q.woche)) {
          this.ed.fokusWoche = q.woche
          this.ed.checked = [q.woche]
          const z = this.zielId && effZuordnung(this.ed.typ, this.zielId, q.woche)
          if (z) this.ed.preset = {azra: z.azra, phase_von: z.phase_von, woche_nr: z.woche_nr}
        }
        if (q.azra) this.ed.preset = {azra: q.azra, phase_von: q.phase || '', woche_nr: Number(q.nr) || 1}
        const step = Number(q.schritt) || (this.$route.hash === '#schritt-5' ? 5 : 0)
        if (step) setTimeout(() => this.springe(step), 300)
      })
    },
    defaultPreset () {
      if (this.ed.typ === 'MACHINE' && this.zielId) {
        const m = modellVonMaschine(this.zielId)
        const g = m && m.gruppen.find(x => x.planungsgruppe_id === this.zielId)
        if (g && g.standard_azra) return {azra: g.standard_azra, phase_von: '', woche_nr: 1}
      }
      if (this.ed.typ === 'CREW' && this.zielId && dp.plan) {
        const r = dp.plan.rotationen.find(x => x.planungsgruppe_id === this.zielId)
        if (r) return {azra: r.azra, phase_von: '', woche_nr: 1}
      }
      return this.ed.preset
    },
    alleWaehlen () {
      const g = this.ed.gewaehlt
      this.ed.checked = this.wochen.filter(w => (this.ed.typ !== 'MACHINE' || this.ed.paritaet === 'ALL' || w.paritaet === this.ed.paritaet) &&
        (!g || inPhase(w.montag, g.phase_von, g.phase_bis))).map(w => w.key)
    },
    zuordnen () {
      const g = this.ed.gewaehlt
      const wochen = this.ed.checked.filter(k => {
        const w = weekInfo(k)
        return inPhase(w.montag, g.phase_von, g.phase_bis) && (this.ed.typ !== 'MACHINE' || this.ed.paritaet === 'ALL' || w.paritaet === this.ed.paritaet)
      })
      for (const k of wochen) {
        stageZuordnung(this.ed.typ, this.zielId, k, g)
        if (this.ed.typ === 'DIRECT') stageBesetzung(this.zielId, k, {typ: 'DIRECT'})
      }
      this.ed.checked = []
      if (wochen.length) this.ed.fokusWoche = wochen[0]
      this.syncTeam()
      this.toast(wochen.length + ' Kalenderwoche(n) vorgemerkt. Weiter mit der Besetzung oder speichern.')
    },
    entfernen () {
      for (const k of this.ed.checked) {
        stageZuordnung(this.ed.typ, this.zielId, k, null)
        if (this.ed.typ === 'DIRECT') {
          const b = effBesetzung(this.zielId, k)
          if (b && b.typ === 'DIRECT') stageBesetzung(this.zielId, k, null)
        }
      }
      this.toast(this.ed.checked.length + ' Entfernung(en) vorgemerkt.')
      this.ed.checked = []
    },
    rueckgaengig (w) {
      discardStaged((kind, v) => kind === 'zuordnungen' && v.ziel_typ === this.ed.typ && v.ziel_id === this.zielId && v.iso_woche === w.key)
      if (this.ed.typ === 'DIRECT') discardStaged((kind, v) => kind === 'besetzungen' && v.mita_bis_id === this.zielId && v.iso_woche === w.key)
    },
    ansehen (w, z) {
      this.ansichtTitel = this.zielName + ' · ' + weekLabel(w) + ' · ' + zuordnungLabel(z)
      this.ansichtTage = z.tage
      this.ansichtMontag = w.montag
      this.$refs.ansicht.open()
    },
    // ----- crew staffing -----
    syncTeam () {
      this.auswahlTeam = this.kolonneTeam.filter(r => r.hier).map(r => r.mita)
    },
    toggleTeam (mita, on) {
      this.auswahlTeam = on ? [...new Set([...this.auswahlTeam, mita])] : this.auswahlTeam.filter(x => x !== mita)
    },
    teamVormerken () {
      const wochen = this.alleWochen ? this.wochen.filter(w => effZuordnung('CREW', this.zielId, w.key)).map(w => w.key) : [this.ed.fokusWoche]
      const plan = []
      for (const woche of wochen) {
        for (const r of this.kolonneTeam) {
          const q = effBesetzung(r.mita, woche)
          const hier = !!q && q.typ === 'CREW' && q.planungsgruppe_id === this.zielId
          const soll = this.auswahlTeam.includes(r.mita)
          if (soll && !hier && inPool(this.zielId, r.mita, woche)) plan.push({mita: r.mita, woche, vorher: q, next: {typ: 'CREW', planungsgruppe_id: this.zielId}})
          if (!soll && hier) plan.push({mita: r.mita, woche, vorher: q, next: null})
        }
      }
      if (!plan.length) {
        this.toast('Keine Änderungen an der Besetzung.', 'grey darken-1')
        return
      }
      this.teamPlan = plan
      this.$refs.team.open()
    },
    teamUebernehmen () {
      this.teamPlan.forEach(t => stageBesetzung(t.mita, t.woche, t.next))
      this.$refs.team.close()
      this.toast(this.teamPlan.length + ' Besetzungsänderung(en) vorgemerkt.')
    },
    wieVorwoche () {
      const i = this.wochen.findIndex(w => w.key === this.ed.fokusWoche)
      const prev = this.wochen[i - 1].key
      const cur = this.ed.fokusWoche
      if (this.ed.typ === 'CREW') {
        this.auswahlTeam = this.kolonneTeam.filter(r => {
          const q = effBesetzung(r.mita, prev)
          return q && q.typ === 'CREW' && q.planungsgruppe_id === this.zielId && r.berechtigt
        }).map(r => r.mita)
        this.toast('Auswahl der Vorwoche übernommen – bitte „Besetzung vormerken“.')
        return
      }
      let n = 0
      for (const p of this.modell.positionen) {
        const vorher = dp.basis.mitarbeiter.find(m => { const q = effBesetzung(m.bis_id, prev); return q && q.typ === 'MACHINE' && q.fahrerposition_id === p.id })
        if (vorher && this.inhaber(p.id) !== vorher.bis_id) {
          this.fahrerSetzenWoche(p.id, vorher.bis_id, cur)
          n++
        }
      }
      this.toast(n + ' Fahrer aus der Vorwoche vorgemerkt.')
    },
    // ----- drivers -----
    inhaber (posId) {
      const m = dp.basis.mitarbeiter.find(x => { const q = effBesetzung(x.bis_id, this.ed.fokusWoche); return q && q.typ === 'MACHINE' && q.fahrerposition_id === posId })
      return m ? m.bis_id : null
    },
    fahrerItems (posId) {
      return dp.basis.mitarbeiter.filter(m => machineEligible(posId, m.bis_id, this.ed.fokusWoche)).map(m => {
        const q = effBesetzung(m.bis_id, this.ed.fokusWoche)
        const busy = q && !(q.typ === 'MACHINE' && q.fahrerposition_id === posId)
        return {value: m.bis_id, text: name(m.bis_id) + (busy ? ' – derzeit ' + quelleLabel(q) : '')}
      })
    },
    fahrerSetzen (posId, mita) {
      const ziel = this.fahrerAlle === 'WOCHE' ? [this.ed.fokusWoche]
        : this.wochen.filter(w => this.fahrerAlle === 'ALLE' || w.paritaet === this.fokusInfo.paritaet).map(w => w.key)
      ziel.forEach(w => this.fahrerSetzenWoche(posId, mita, w))
      this.toast('Fahrerwechsel für ' + ziel.length + ' Woche(n) vorgemerkt.')
    },
    fahrerSetzenWoche (posId, mita, woche) {
      const bisher = dp.basis.mitarbeiter.find(x => { const q = effBesetzung(x.bis_id, woche); return q && q.typ === 'MACHINE' && q.fahrerposition_id === posId })
      if (bisher && bisher.bis_id !== mita) stageBesetzung(bisher.bis_id, woche, null)
      if (mita) stageBesetzung(mita, woche, {typ: 'MACHINE', fahrerposition_id: posId})
    },
    // stage the default pattern of every machine without a pattern week; the
    // phase containing the week decides the pattern week (week 1 of the phase)
    maschinenErgaenzen () {
      let n = 0
      let ohne = []
      const jobs = this.fehlendeMaschinen.map(f => {
        if (!f.azra) {
          ohne.push(gruppeName(f.id))
          return Promise.resolve()
        }
        return rasterWochen(f.azra).then(data => {
          for (const key of f.wochen) {
            const w = weekInfo(key)
            const ph = data.phasen.find(p => inPhase(w.montag, p.von, p.bis))
            const pw = ph && ph.wochen[0]
            if (!pw || pw.fehler) continue
            stageZuordnung('MACHINE', f.id, key, {azra: f.azra, raster_name: data.name, phase_von: ph.von, phase_bis: ph.bis, woche_nr: pw.nr, hash: pw.hash, tage: pw.tage})
            n++
          }
        })
      })
      Promise.all(jobs).then(() => {
        this.toast(n + ' Maschinen-Musterwoche(n) vorgemerkt' + (ohne.length ? '; ohne Standardraster: ' + ohne.join(', ') : '') + '.', ohne.length ? 'orange darken-2' : 'success')
      }, res => this.fail(res))
    },
    dienstText (posId) {
      const r = positionWoche(posId, this.ed.fokusWoche)
      return r.tage.map((d, i) => d.maschine ? WOCHENTAGE_KURZ[i] + ' ' + gruppeName(d.maschine) : '').filter(Boolean).join(', ') || 'keine Dienste'
    },
    positionAnsehen (p) {
      const r = positionWoche(p.id, this.ed.fokusWoche)
      this.ansichtTitel = p.bezeichnung + ' · ' + this.fokusWocheLabel + ' · kombinierte Maschinendienste'
      this.ansichtTage = r.tage
      this.ansichtMontag = this.fokusInfo.montag
      this.$refs.ansicht.open()
    },
    slot (machineId, i) {
      const w = this.fokusInfo
      const z = this.modell.matrix.find(x => x.planungsgruppe_id === machineId && x.wochenparitaet === w.paritaet && x.wochentag === i + 1)
      if (!z) return {text: 'kein Dienst', sub: '', cls: 'none'}
      const pos = this.modell.positionen.find(p => p.id === z.fahrerposition_id)
      const map = effZuordnung('MACHINE', machineId, w.key)
      const day = map && map.tage[i]
      const owner = this.inhaber(z.fahrerposition_id)
      const needed = day && day.zeit_soll > 0
      return {text: (pos || {}).bezeichnung, sub: (owner ? name(owner) : 'unbesetzt') + ' · ' + (map ? (needed ? day.soll_zeit : 'Maschine frei') : 'keine Musterwoche'),
        cls: !map || !needed ? 'none' : owner ? 'ok' : 'empty'}
    },
    // ----- save -----
    speichernOeffnen () {
      this.$refs.speichern.open()
    },
    speichern () {
      this.busy = true
      const summary = [...new Set(this.rows.map(r => r.ziel))].slice(0, 4).join(', ') + ': ' + this.dirty + ' Änderung(en)'
      saveDraft(summary).then(() => {
        this.busy = false
        this.$refs.speichern.close()
        this.toast('Entwurf gespeichert. Es wurde nichts veröffentlicht.')
        this.syncTeam()
      }, res => {
        this.busy = false
        if (res && res.status === 409) this.$refs.speichern.close()
        this.toast(errorText(res), 'red accent-2')
      })
    },
    verwerfen () {
      discardStaged()
      this.$refs.verwerfen.close()
      this.syncTeam()
      this.toast('Vorgemerkte Änderungen verworfen.', 'grey darken-1')
    },
  },
}
</script>

<style scoped>
.dp-steps {
  display: flex;
  overflow: auto;
  border-bottom: 1px solid #cfd8dc;
  background: #fff;
  padding: 0 12px;
}
.dp-steps button {
  border: 0;
  border-bottom: 2px solid transparent;
  background: transparent;
  padding: 10px 14px;
  font-size: 12px;
  color: #546e7a;
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 8px;
}
.dp-steps button.active {
  border-bottom-color: #3f51b5;
  color: #3f51b5;
}
.dot {
  display: inline-grid;
  place-items: center;
  width: 21px;
  height: 21px;
  border-radius: 50%;
  border: 1px solid #c5cae9;
  background: #f5f6fc;
  font-size: 10px;
  color: #3f51b5;
}
.dp-sec {
  margin-bottom: 22px;
  scroll-margin-top: 10px;
}
.dp-sec + .dp-sec {
  border-top: 1px solid #e0e0e0;
  padding-top: 18px;
}
.dp-sec h2 {
  font-size: 15px;
  font-weight: 600;
  margin: 0 0 12px;
  display: flex;
  align-items: center;
  gap: 9px;
  flex-wrap: wrap;
}
.dp-sec h2 small {
  font-size: 11px;
  font-weight: 400;
  color: #757575;
  margin-left: auto;
}
.num {
  display: inline-grid;
  place-items: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #e8eaf6;
  color: #3f51b5;
  font-size: 12px;
}
.dp-hint {
  display: flex;
  gap: 8px;
  align-items: flex-start;
  font-size: 12px;
  color: #455a64;
}
.dp-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  border-top: 1px solid #e0e0e0;
  margin-top: 12px;
  padding-top: 12px;
}
.dp-table tr.chosen {
  background: #f5f7ff;
}
.dp-metrics {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  margin-bottom: 12px;
}
.dp-metrics div {
  border: 1px solid #dfe3e8;
  background: #fafbfc;
  border-radius: 3px;
  padding: 10px 14px;
  font-size: 12px;
  color: #607d8b;
}
.dp-metrics b {
  display: block;
  font-size: 22px;
  font-weight: 500;
  color: #263238;
}
.slot {
  border: 1px solid #c5cae9;
  background: #f1f3fc;
  border-radius: 3px;
  padding: 4px;
  font-size: 11px;
  min-width: 96px;
}
.slot small {
  display: block;
  color: #607d8b;
}
.slot.empty {
  border-color: #ffcc80;
  background: #fff8e1;
  color: #e65100;
}
.slot.none {
  border-style: dashed;
  border-color: #e0e0e0;
  background: #fafafa;
  color: #9e9e9e;
}
</style>
